import { InputAdornment } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { FormAwareButtonGroup } from '@/components/form/formAwareInputs/FormAwareButtonGroup';
import { FormAwareCurrencyInput } from '@/components/form/formAwareInputs/FormAwareCurrencyInput';
import { FormAwarePercentInput } from '@/components/form/formAwareInputs/FormAwarePercentInput';
import { FormAwareRadioGroup } from '@/components/form/formAwareInputs/FormAwareRadioGroup';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { FormAwareTypeaheadSelectInput } from '@/components/form/formAwareInputs/FormAwareTypeaheadSelectInput';
import { User01Icon } from '@/components/icons/User01Icon';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { useFormContext } from '@/components/react-hook-form';
import { ACCESS_PARAMETER_FREQUENCY_ITEMS } from '@/modules/beneficiaries/beneficiaries.constants';
import { AddNewAnythingModal } from '@/modules/common/AddNewAnythingModal/AddNewAnythingModal';
import { EntityDocumentViewerDocument } from '@/modules/documents/components/HouseholdDocumentsViewer/graphql/EntityDocumentViewer.generated';
import { AccessParameterKind } from '@/types/schema';

import {
  UNITRUST_TYPE_ITEMS_V2,
  validUnitrustTypesV2,
} from './EntityBeneficiariesSubform.constants';
import {
  EntityBeneficiariesFormBeneficiary,
  EntityBeneficiariesFormBodyProps,
  EntityBeneficiariesSubformFields,
  EntityBeneficiariesSubformSubformPath,
} from './EntityBeneficiariesSubform.types';

interface CharitableBeneficiaryFormBodyProps {
  control: EntityBeneficiariesFormBodyProps['control'];
  beneficiaryOptions: EntityBeneficiariesFormBodyProps['beneficiaryOptions'];
  householdId: EntityBeneficiariesFormBodyProps['householdId'];
  namespace:
    | 'leadBeneficiarySubform.leadBeneficiary'
    | 'incomeBeneficiariesSubform.incomeBeneficiaries';
  index?: number;
  splitScreen: boolean;
}

export function CharitableBeneficiaryFormBodyV2({
  control,
  beneficiaryOptions,
  householdId,
  namespace,
  index,
  splitScreen,
}: CharitableBeneficiaryFormBodyProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [newlyCreatedId, setNewlyCreatedId] = useState('');

  const isClt = namespace === 'leadBeneficiarySubform.leadBeneficiary';
  const isCrt = namespace === 'incomeBeneficiariesSubform.incomeBeneficiaries';

  // For CLT, leadBeneficiary is a single object so we use the namespace directly.
  // For CRT, incomeBeneficiaries is an array so we need to include the index.
  const baseNamespace: string = isClt
    ? namespace
    : `${namespace}.${index ?? 0}`;

  const { setValue, setShouldBlockNavigation } =
    useFormContext<EntityBeneficiariesSubformFields>();

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  // For CRT, charitableBeneficiaryType is stored at the subform level (shared across all income beneficiaries)
  // For CLT, it's stored at the individual beneficiary level
  const charitableBeneficiaryTypeFieldName = isCrt
    ? 'incomeBeneficiariesSubform.charitableBeneficiaryType'
    : `${baseNamespace}.charitableBeneficiaryType`;

  const watchedTab = useWatch({
    name: charitableBeneficiaryTypeFieldName as EntityBeneficiariesSubformSubformPath,
    control,
  }) as 'annuity' | 'unitrust' | undefined;

  // Default to 'annuity' if no value is set
  const currentTab = watchedTab ?? 'annuity';

  // our app currently makes the assumption that there will only be one distribution for a given
  // charitable beneficiary, so we can assume index 0.
  const beneficiaryType = useWatch({
    name: `${baseNamespace}.beneficiaryFormAccessParameters.amountsOrPercentages.0.kind` as EntityBeneficiariesSubformSubformPath,
    control,
  }) as AccessParameterKind | undefined;

  const currentBeneficiary = useWatch({
    name: baseNamespace as EntityBeneficiariesSubformSubformPath,
    control,
  }) as EntityBeneficiariesFormBeneficiary | undefined;

  // generally speaking, it's not required to select a beneficiary. however, we want to make sure that
  // a user isn't losing valuable data that they've entered because they forget to select the underlying beneficiary.
  // so, we mark the beneficiary dropdown as "required" if the user has entered data in what we deem to be
  // "high-value" fields in the subform.
  const beneficiarySelectionRequired = useMemo(() => {
    if (!currentBeneficiary) return false;
    if (currentTab === 'unitrust') {
      return Boolean(
        currentBeneficiary?.beneficiaryFormAccessParameters
          ?.amountsOrPercentages?.[0]?.percentage
      );
    } else {
      return Boolean(
        currentBeneficiary?.beneficiaryFormAccessParameters
          ?.amountsOrPercentages?.[0]?.amount
      );
    }
  }, [currentBeneficiary, currentTab]);

  useEffect(
    function setTypeWhenUnitrust() {
      if (currentTab === 'unitrust') {
        // set the type to percentage if the selected type isn't valid for unitrust
        if (
          !beneficiaryType ||
          !validUnitrustTypesV2.includes(beneficiaryType)
        ) {
          setValue(
            // our app currently makes the assumption that there will only be one distribution for a given
            // charitable beneficiary, so we can assume index 0.
            `${baseNamespace}.beneficiaryFormAccessParameters.amountsOrPercentages.0.kind` as EntityBeneficiariesSubformSubformPath as never,
            AccessParameterKind.Percentage as never,
            { shouldDirty: true }
          );
        }
      } else {
        setValue(
          // our app currently makes the assumption that there will only be one distribution for a given
          // charitable beneficiary, so we can assume index 0.
          `${baseNamespace}.beneficiaryFormAccessParameters.amountsOrPercentages.0.kind` as EntityBeneficiariesSubformSubformPath as never,
          AccessParameterKind.Amount as never,
          { shouldDirty: true }
        );
      }
    },
    [currentTab, beneficiaryType, setValue, baseNamespace]
  );

  useEffect(() => {
    if (splitScreen && newlyCreatedId && !isOpen) {
      // Closing the modal after creating a new entity/individual/organization
      // should block navigation for the parent form.
      setShouldBlockNavigation(true);
      setNewlyCreatedId('');
    }
  }, [newlyCreatedId, setShouldBlockNavigation, isOpen, splitScreen]);

  return (
    <>
      <AddNewAnythingModal
        addableTypes={['entity', 'individual', 'organization']}
        handleClose={handleClose}
        isOpen={isOpen}
        householdId={householdId}
        onAfterCreate={setNewlyCreatedId}
        addClientProfileModalProps={{
          ignoredQueryDocuments: [EntityDocumentViewerDocument],
        }}
        addClientOrganizationModalProps={{
          ignoredQueryDocuments: [EntityDocumentViewerDocument],
        }}
      />
      {/* For CRT, the Annuity/Unitrust button group is rendered once at the top of the list in UI.tsx */}
      {isClt && (
        <FormLayoutRow>
          <FormLayoutItem width={12}>
            <FormAwareButtonGroup<EntityBeneficiariesSubformFields>
              label=""
              fieldName={`${baseNamespace}.charitableBeneficiaryType` as never}
              control={control}
              options={[
                {
                  display: 'Annuity',
                  value: 'annuity',
                },
                {
                  display: 'Unitrust',
                  value: 'unitrust',
                },
              ]}
            />
          </FormLayoutItem>
        </FormLayoutRow>
      )}
      {currentTab === 'annuity' && (
        <>
          <FormLayoutRow>
            <FormLayoutItem width={8}>
              <FormAwareCurrencyInput<EntityBeneficiariesSubformFields>
                required
                label="Yearly distribution amount"
                fieldName={
                  `${baseNamespace}.beneficiaryFormAccessParameters.amountsOrPercentages.0.amount` as never
                }
                control={control}
                isDecimalJSInput
              />
            </FormLayoutItem>
            <FormLayoutItem width={4}>
              <FormAwareSelectInput<EntityBeneficiariesSubformFields>
                label="Distribution frequency"
                fieldName={
                  `${baseNamespace}.beneficiaryFormAccessParameters.amountsOrPercentages.0.frequency` as never
                }
                control={control}
                options={ACCESS_PARAMETER_FREQUENCY_ITEMS}
              />
            </FormLayoutItem>
          </FormLayoutRow>
          <FormLayoutRow>
            <FormLayoutItem width={12}>
              <FormAwareTypeaheadSelectInput
                control={control}
                required={beneficiarySelectionRequired}
                label="Beneficiary"
                fieldName={`${baseNamespace}.beneficiaryId` as never}
                addNewOption={{
                  onClick: () => setIsOpen(true),
                }}
                options={beneficiaryOptions}
                placeholder="Search beneficiaries"
                groupBy={(option) => option.groupName ?? ''}
                startAdornment={
                  <InputAdornment position="start">
                    <User01Icon size={16} />
                  </InputAdornment>
                }
              />
            </FormLayoutItem>
          </FormLayoutRow>
        </>
      )}
      {currentTab === 'unitrust' && (
        <>
          {!isClt && (
            <FormLayoutRow>
              <FormLayoutItem width={12}>
                <FormAwareRadioGroup<EntityBeneficiariesSubformFields>
                  label=""
                  fieldName={
                    `${baseNamespace}.beneficiaryFormAccessParameters.amountsOrPercentages.0.kind` as never
                  }
                  control={control}
                  options={UNITRUST_TYPE_ITEMS_V2}
                  row
                />
              </FormLayoutItem>
            </FormLayoutRow>
          )}
          <FormLayoutRow>
            <FormLayoutItem width={8}>
              <FormAwarePercentInput<EntityBeneficiariesSubformFields>
                required
                label="Yearly distribution percentage"
                fieldName={
                  `${baseNamespace}.beneficiaryFormAccessParameters.amountsOrPercentages.0.percentage` as never
                }
                control={control}
                isDecimalJSInput
              />
            </FormLayoutItem>
            <FormLayoutItem width={4}>
              <FormAwareSelectInput<EntityBeneficiariesSubformFields>
                label="Distribution frequency"
                fieldName={
                  `${baseNamespace}.beneficiaryFormAccessParameters.amountsOrPercentages.0.frequency` as never
                }
                control={control}
                options={ACCESS_PARAMETER_FREQUENCY_ITEMS}
              />
            </FormLayoutItem>
          </FormLayoutRow>
          <FormLayoutRow>
            <FormLayoutItem width={12}>
              <FormAwareTypeaheadSelectInput
                control={control}
                label="Beneficiary"
                required={beneficiarySelectionRequired}
                fieldName={`${baseNamespace}.beneficiaryId` as never}
                addNewOption={{
                  onClick: () => setIsOpen(true),
                }}
                options={beneficiaryOptions}
                placeholder="Search beneficiaries"
                groupBy={(option) => option.groupName ?? ''}
                startAdornment={
                  <InputAdornment position="start">
                    <User01Icon size={16} />
                  </InputAdornment>
                }
              />
            </FormLayoutItem>
          </FormLayoutRow>
        </>
      )}
    </>
  );
}
